import {
  SafeDepositBoxFields,
  SafeDepositBoxServiceAgreementProperties,
  SafeLocation,
  SafeSize,
} from 'pco-product-service-internal-consumer';
import { isSafeDepositBoxServiceAgreementProperties } from '../../../productSpecificProperties';

export type SafeDepositBoxDetailsDraft = {
  safeLocation: SafeLocation | '';
  safeSize: SafeSize | '';
  safeNr: string;
  keyDeposit: boolean;
};

export const SAFE_DEPOSIT_BOX_SAFE_NUMBER_MAX_LENGTH = 10;
const SAFE_DEPOSIT_BOX_SAFE_NUMBER_PATTERN = /^\d{1,10}$/;

export type SafeDepositBoxDetailsField = 'safeLocation' | 'safeSize' | 'safeNr';

export type SafeDepositBoxSafeNumberError = 'required' | 'invalid' | null;
export type SafeDepositBoxRequiredError = 'required' | null;

export type SafeDepositBoxDetailsErrors = {
  safeLocation: SafeDepositBoxRequiredError;
  safeSize: SafeDepositBoxRequiredError;
  safeNr: SafeDepositBoxSafeNumberError;
};

export const NO_SAFE_DEPOSIT_BOX_DETAILS_ERRORS: SafeDepositBoxDetailsErrors = {
  safeLocation: null,
  safeSize: null,
  safeNr: null,
};

export const getSafeDepositBoxSafeNumberError = (safeNr: string): SafeDepositBoxSafeNumberError => {
  if (safeNr.length === 0) return 'required';
  return SAFE_DEPOSIT_BOX_SAFE_NUMBER_PATTERN.test(safeNr) ? null : 'invalid';
};

export const isValidSafeDepositBoxSafeNumber = (safeNr: string) => getSafeDepositBoxSafeNumberError(safeNr) === null;

/**
 * A missing value is only reported once the field has been touched, so opening a box that was never
 * configured does not greet the user with three "required" alerts. A value that is present but malformed
 * was necessarily typed, so it is always reported.
 */
export const getSafeDepositBoxDetailsErrors = (
  draft: SafeDepositBoxDetailsDraft,
  touched: ReadonlySet<SafeDepositBoxDetailsField>,
): SafeDepositBoxDetailsErrors => {
  const safeNrError = getSafeDepositBoxSafeNumberError(draft.safeNr);

  return {
    safeLocation: !draft.safeLocation && touched.has('safeLocation') ? 'required' : null,
    safeSize: !draft.safeSize && touched.has('safeSize') ? 'required' : null,
    safeNr: safeNrError === 'required' && !touched.has('safeNr') ? null : safeNrError,
  };
};

export const EMPTY_SAFE_DEPOSIT_BOX_DETAILS_DRAFT: SafeDepositBoxDetailsDraft = {
  safeLocation: '',
  safeSize: '',
  safeNr: '',
  keyDeposit: false,
};

/**
 * The schema marks the four fields required, but rows written before that change can still carry
 * missing values at runtime, so every field falls back to the empty draft value.
 */
export const buildSafeDepositBoxDetailsDraft = (
  properties: SafeDepositBoxServiceAgreementProperties | undefined,
): SafeDepositBoxDetailsDraft => {
  if (!isSafeDepositBoxServiceAgreementProperties(properties)) return EMPTY_SAFE_DEPOSIT_BOX_DETAILS_DRAFT;

  return {
    safeLocation: properties.safeLocation ?? '',
    safeSize: properties.safeSize ?? '',
    safeNr: properties.safeNr ?? '',
    keyDeposit: properties.keyDeposit ?? false,
  };
};

const normalizedDetailsSnapshot = (draft: SafeDepositBoxDetailsDraft) => ({
  safeLocation: draft.safeLocation || null,
  safeSize: draft.safeSize || null,
  safeNr: draft.safeNr,
  keyDeposit: draft.keyDeposit,
});

export const haveSameSafeDepositBoxDetails = (left: SafeDepositBoxDetailsDraft, right: SafeDepositBoxDetailsDraft) =>
  JSON.stringify(normalizedDetailsSnapshot(left)) === JSON.stringify(normalizedDetailsSnapshot(right));

/** Safe location, safe size and safe number are mandatory; key deposit is not, so `false` is a complete answer. */
export const isSafeDepositBoxDetailsComplete = (draft: SafeDepositBoxDetailsDraft) =>
  !!draft.safeLocation && !!draft.safeSize && isValidSafeDepositBoxSafeNumber(draft.safeNr);

export const buildSafeDepositBoxDetailsPayload = (draft: SafeDepositBoxDetailsDraft): SafeDepositBoxFields => ({
  ...(draft.safeLocation ? { safeLocation: draft.safeLocation as SafeLocation } : {}),
  ...(draft.safeSize ? { safeSize: draft.safeSize as SafeSize } : {}),
  ...(draft.safeNr ? { safeNr: draft.safeNr } : {}),
  keyDeposit: draft.keyDeposit,
});
